import { Component, Inject, OnInit } from "@angular/core";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { Data, Router } from "@angular/router";
import { AuthService } from "@core/auth.service";
import { AuthPartialRegService } from "@core/AuthServices/authPartialReg/auth-partial-reg.service";
import { IonicModule } from "@ionic/angular";
import { TranslatePipe, TranslateService } from "@ngx-translate/core";
import { take } from "rxjs/operators";
import { CommonVariable } from "src/app/app.constants";
import { PaymentService } from "src/app/services/payment.service";
import {
  DEFAULT_NO_THANKS_ACCENT_COLOR,
  getDefaultVariantContent,
  getNonEmptyBulletPoints,
  getTranslatedValue,
  getTranslatedValueWithEnglishFallback,
  isValidHttpUrl,
  isValidPriceToken,
  resolveHexColor,
  resolveNoThanksVariant,
  sanitizeResolvedPriceValue,
} from "./no-thanks-popup.helpers";

@Component({
  selector: "app-no-thanks-popup",
  templateUrl: "./no-thanks-popup.component.html",
  styleUrls: ["./no-thanks-popup.component.scss"],
  standalone: true,
  imports: [IonicModule, TranslatePipe],
})
export class NoThanksPopupComponent implements OnInit {
  prosubId: string | null = null;
  modalHeight: string = "40%";
  defaultData: boolean;
  newOfferProsub: string = "4375";
  sureText: string = "";
  buttonText: string = "";
  preferredCurrency: string;
  currencySymbol: string = CommonVariable.defaultCurrencySymbol;
  noThanksDetails: Data;
  selectedLanguage: string;
  noThanksUrl = "";
  showLogo = true;
  isLoadingPopup = true;
  showMainImage = true;
  redRibbonText = "";
  variant1Title = "";
  variant1BulletPoints: string[] = [];
  resolvedFullPrice = "";
  resolvedActualPrice = "";
  resolvedCtaLabel = "";
  ctaSubstring = "";
  textLinkDestination = "";
  actualPriceColour = DEFAULT_NO_THANKS_ACCENT_COLOR;
  ctaColour = DEFAULT_NO_THANKS_ACCENT_COLOR;

  get hasValidLogoUrl(): boolean {
    if (!this.showLogo) {
      return false;
    }

    return isValidHttpUrl(this.noThanksDetails?.logo_url);
  }

  get hasValidMainImageUrl(): boolean {
    if (!this.showMainImage) {
      return false;
    }

    return isValidHttpUrl(this.noThanksDetails?.main_image_url);
  }

  get showRedRibbon(): boolean {
    return !!this.redRibbonText;
  }

  get showVariant1Title(): boolean {
    return !!this.variant1Title;
  }

  get showBulletSection(): boolean {
    return this.variant1BulletPoints.length > 0;
  }

  get showFullPrice(): boolean {
    return !!this.resolvedFullPrice;
  }

  get showActualPrice(): boolean {
    return !!this.resolvedActualPrice;
  }

  get showPriceComparison(): boolean {
    return this.showFullPrice || this.showActualPrice;
  }

  get showPriceArrow(): boolean {
    return this.showFullPrice && this.showActualPrice;
  }

  get showCtaSubstring(): boolean {
    return !!this.ctaSubstring;
  }

  constructor(
    public dialogRef: MatDialogRef<NoThanksPopupComponent>,
    private authService: AuthService,
    private router: Router,
    public paymentService: PaymentService,
    public translate: TranslateService,
    public authpartialRegService: AuthPartialRegService,
    @Inject(MAT_DIALOG_DATA) public data: any
  ) {}

  ngOnInit() {
    this.dialogRef.addPanelClass("no-thanks-dialog");
    this.defaultData = true;
    this.authService.selectedlanguage$.subscribe(
      (l) => {
        const selectedLanguage = l || "en";
        const finalLanguage = CommonVariable.bypassLanguages.includes(selectedLanguage)
          ? "en"
          : selectedLanguage;
        this.selectedLanguage = finalLanguage;
      },
      () => {
        this.selectedLanguage = "en";
      }
    );
    const urlParams = new URLSearchParams(window.location.search);
    this.prosubId = urlParams.get("prosub_id");
    this.authService.getLSWProsubDetails(this.prosubId).subscribe(
      (prosubDetails: Data | null) => {
        this.initializeNoThanksPopup(prosubDetails);
      },
      () => {
        this.defaultData = true;
        this.noThanksDetails = {
          variant: 0,
          title: "",
          description: "no-thanks-popup.sure",
          cta_label: "no-thanks-popup.back",
          no_thanks: "no-thanks-popup.want",
        };
        this.finishPopupLoad();
      }
    );

    this.sureText = this.data?.sureText || "";
    this.buttonText = this.data?.buttonText || "";
    this.noThanksUrl = this.data?.noThanksUrl || "";
  }

  private initializeNoThanksPopup(prosubDetails: Data | null) {
    const noThanksDetails = prosubDetails?.no_thanks_details;
    const lang = this.selectedLanguage || "en";

    if (!noThanksDetails) {
      this.defaultData = true;
      this.noThanksDetails = {
        variant: 0,
        title: "",
        description: "no-thanks-popup.sure",
        cta_label: "no-thanks-popup.back",
        no_thanks: "no-thanks-popup.want",
      };
      this.finishPopupLoad();
      return;
    }

    this.showLogo = true;
    this.showMainImage = true;
    this.defaultData = false;
    const variantNum = resolveNoThanksVariant(noThanksDetails.variant);

    if (variantNum === 1) {
      this.initializeVariant1(noThanksDetails, lang);
      return;
    }

    this.initializeVariant0(noThanksDetails, lang);
  }

  private initializeVariant0(noThanksDetails: Data, lang: string) {
    this.modalHeight = "55%";
    this.dialogRef.updateSize("80%", this.modalHeight);

    const defaultContent = getDefaultVariantContent(noThanksDetails);
    const rawCta =
      defaultContent?.cta_label?.[lang] || defaultContent?.cta_label?.["en"];
    const rawDesc =
      defaultContent?.description?.[lang] || defaultContent?.description?.["en"];

    Promise.all([
      this.authService.getDVFromString(rawCta),
      this.authService.getDVFromString(rawDesc),
    ]).then(([resolvedCta, resolvedDesc]) => {
      this.noThanksDetails = {
        variant: 0,
        title: defaultContent?.title || {},
        cta_label: resolvedCta,
        description: resolvedDesc,
        no_thanks: defaultContent?.no_thanks || {},
        cta_link: noThanksDetails.cta_link || "",
      };
      this.finishPopupLoad();
    });
  }

  private initializeVariant1(noThanksDetails: Data, lang: string) {
    const variant1 = noThanksDetails?.variants?.variant1;
    if (!variant1) {
      this.initializeVariant0(noThanksDetails, lang);
      return;
    }

    this.modalHeight = "auto";
    this.dialogRef.updateSize("90%", this.modalHeight);
    this.dialogRef.addPanelClass("transparent-modal");

    this.redRibbonText = getTranslatedValue(variant1.red_ribbon_text, lang);
    this.variant1Title = getTranslatedValue(variant1.title, lang);
    this.variant1BulletPoints = getNonEmptyBulletPoints(variant1.bullet_points, lang);
    this.ctaSubstring = getTranslatedValue(variant1.cta_substring, lang);
    this.textLinkDestination = getTranslatedValueWithEnglishFallback(variant1.text_link, lang);
    this.actualPriceColour = resolveHexColor(variant1.actual_price_colour);
    this.ctaColour = resolveHexColor(variant1.cta_colour);

    this.noThanksDetails = {
      variant: 1,
      cta_link: noThanksDetails.cta_link || "",
      logo_url: variant1.logo_url || "",
      main_image_url: variant1.main_image_url || "",
    };

    const resolutionTasks: Promise<void>[] = [];
    const rawFullPrice = variant1.full_price;
    const rawActualPrice = variant1.actual_price;
    const rawCtaLabel = getTranslatedValueWithEnglishFallback(variant1.cta_label, lang);

    if (isValidPriceToken(rawFullPrice)) {
      resolutionTasks.push(
        this.authService
          .getDVFromString(rawFullPrice)
          .then((resolved) => {
            this.resolvedFullPrice = sanitizeResolvedPriceValue(resolved, rawFullPrice);
          })
          .catch(() => {
            this.resolvedFullPrice = "";
          })
      );
    } else {
      this.resolvedFullPrice = "";
    }

    if (isValidPriceToken(rawActualPrice)) {
      resolutionTasks.push(
        this.authService
          .getDVFromString(rawActualPrice)
          .then((resolved) => {
            this.resolvedActualPrice = sanitizeResolvedPriceValue(resolved, rawActualPrice);
          })
          .catch(() => {
            this.resolvedActualPrice = "";
          })
      );
    } else {
      this.resolvedActualPrice = "";
    }

    if (rawCtaLabel) {
      resolutionTasks.push(
        this.authService
          .getDVFromString(rawCtaLabel)
          .then((resolved) => {
            this.resolvedCtaLabel =
              typeof resolved === "string" && resolved.trim() ? resolved.trim() : rawCtaLabel;
          })
          .catch(() => {
            this.resolvedCtaLabel = rawCtaLabel;
          })
      );
    } else {
      this.resolvedCtaLabel = "";
    }

    if (resolutionTasks.length) {
      Promise.all(resolutionTasks).catch(() => undefined);
    }

    this.finishPopupLoad();
  }

  onCtaSubstringClick() {
    this.dialogRef.close(true);

    const destination = this.textLinkDestination?.trim();
    if (!destination) {
      this.callInt2();
      return;
    }

    if (isValidHttpUrl(destination)) {
      window.location.href = destination;
      return;
    }

    if (destination.startsWith("/")) {
      const [path, queryString] = destination.split("?");
      const queryParams = queryString
        ? Object.fromEntries(new URLSearchParams(queryString))
        : {};
      this.router.navigate([path], { queryParams });
      return;
    }

    this.callInt2();
  }

  onLogoLoadError() {
    this.showLogo = false;
  }

  onMainImageLoadError() {
    this.showMainImage = false;
  }

  back(ctaLink?) {
    if (this.noThanksUrl) {
      this.dialogRef.close(false);
      const [path, queryString] = this.noThanksUrl.split("?");
      const queryParams = queryString
        ? Object.fromEntries(new URLSearchParams(queryString))
        : {};
      this.router.navigate([path], { queryParams });
    } else {
      this.dialogRef.close(false);
      const urlParams = new URLSearchParams(window.location.search);
      if (ctaLink) {
        urlParams.delete("prosub_id");
        urlParams.append("prosub_id", ctaLink);
        window.location.href = `/free-reg-step4c?${urlParams.toString()}`;
      } else {
        this.dialogRef.close(false);
      }
    }
  }

  close() {
    this.callInt2();
    this.dialogRef.close(true);
  }

  private finishPopupLoad(): void {
    this.isLoadingPopup = false;
  }

  async callInt2() {
    this.paymentService.getOptionalPage("no-thanks").then(async (resp) => {
      const post_name = resp;
      if (post_name === "free-reg-complete-profile") {
        localStorage.setItem("flowPath", "no_thanks");
        try {
          this.authService.userDetails$.pipe(take(1)).subscribe(
            (user: Data) => {
              if (user?.minimal_required == true) {
                this.paymentService.freeRegJourneyStep = "free-reg-journey-4c";
                this.router.navigateByUrl("/mainTabs/tabs/home");
              } else {
                this.paymentService.freeRegJourneyStep = "free-reg-journey-4c";
                this.paymentService.freeRegJourneySuccessURL = post_name;
                this.router.navigateByUrl(post_name);
              }
            },
            () => {
              this.authService.getConstantsMsg("1013").then((translatedMsg) => {
                const defaultMsg = {
                  message: {
                    line_1: "Something went wrong (user)",
                    line_2: "",
                  },
                  style: {
                    icon: "tick circle error.svg",
                    type: CommonVariable.error,
                  },
                };
                this.authService.showToastMessage(translatedMsg, defaultMsg);
              });
            }
          );
        } catch {
          this.authService.getConstantsMsg("1014").then((translatedMsg) => {
            const defaultMsg = {
              message: {
                line_1: "Something went wrong",
                line_2: "",
              },
              style: {
                icon: "tick circle error.svg",
                type: CommonVariable.error,
              },
            };
            this.authService.showToastMessage(translatedMsg, defaultMsg);
          });
        }
      } else {
        localStorage.setItem("4cpage", "free-reg-step-4c");
        const path = post_name.split("?");
        this.router.navigate([path[0]], {
          queryParams: {
            prosub_id: path[1].split("=")[1],
          },
        });
      }
    });
  }
}
